import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useFollows } from '@/lib/follows';
import { useSEO } from '@/lib/useSEO';
import { formatDate } from '@/lib/format';
import { UserPlus, UserMinus, Store, Award, MapPin, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function Follows() {
  const [items, setItems] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toggle, refresh } = useFollows();

  useSEO({
    title: 'Vendedores que sigo | AutoMarket Colombia',
    description: 'Sigue a tus tiendas favoritas y recibe sus últimas publicaciones en tu feed.',
  });

  useEffect(() => {
    Promise.all([
      api.get('/me/follows').then((r) => r.data.items || []),
      api.get('/me/feed').then((r) => r.data.items || []),
    ]).then(([follows, feedItems]) => {
      setItems(follows);
      setFeed(feedItems);
    }).finally(() => setLoading(false));
  }, []);

  const unfollow = async (sid) => {
    await toggle(sid);
    setItems((arr) => arr.filter((x) => x.seller_id !== sid));
    refresh();
    toast.success('Has dejado de seguir al vendedor');
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Mi cuenta</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 flex items-center gap-2">
          <UserPlus className="h-7 w-7 text-blue-600" /> Vendedores que sigo
        </h1>
        <p className="mt-2 text-sm text-slate-600">Recibe las últimas publicaciones de tus tiendas favoritas.</p>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-8">
          {/* Sellers list */}
          <div>
            {!loading && items.length === 0 && (
              <div data-testid="follows-empty" className="border border-slate-200 bg-slate-50 p-12 text-center">
                <Store className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-4 text-slate-600">Aún no sigues a ningún vendedor.</p>
                <Link to="/catalogo" className="mt-4 inline-block">
                  <Button className="rounded-none bg-slate-900 hover:bg-slate-800">Descubrir vendedores</Button>
                </Link>
              </div>
            )}
            {items.length > 0 && (
              <div data-testid="follows-list" className="border border-slate-200 bg-white divide-y divide-slate-200">
                {items.map((s) => (
                  <div key={s.seller_id} className="flex items-center gap-4 p-4" data-testid={`follow-row-${s.seller_id}`}>
                    <Link
                      to={`/tienda/${s.seller_id}`}
                      className={`flex h-12 w-12 items-center justify-center text-lg font-black ${
                        s.plan === 'pro' ? 'bg-orange-500 text-slate-900' : 'bg-slate-900 text-white'
                      }`}
                    >
                      {s.business_name?.[0]?.toUpperCase() || 'V'}
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/tienda/${s.seller_id}`} className="block">
                        <div className="font-display font-bold text-slate-900 hover:text-blue-700 truncate flex items-center gap-2">
                          {s.business_name}
                          {s.plan === 'pro' && (
                            <span className="inline-flex items-center gap-1 bg-orange-500 text-slate-900 text-[9px] font-bold uppercase tracking-[0.2em] px-1.5 py-0.5">
                              <Award className="h-2.5 w-2.5" /> PRO
                            </span>
                          )}
                        </div>
                      </Link>
                      <div className="mt-0.5 text-xs text-slate-500 flex flex-wrap items-center gap-x-3">
                        {s.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.city}</span>}
                        <span className="inline-flex items-center gap-1"><Package className="h-3 w-3" />{s.products_total} productos</span>
                        <span>Sigues desde {formatDate(s.since).slice(0, 10)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => unfollow(s.seller_id)}
                      data-testid={`unfollow-${s.seller_id}`}
                      className="inline-flex items-center gap-1 border border-slate-300 hover:border-rose-500 hover:text-rose-600 text-xs font-bold uppercase tracking-wider px-3 py-2"
                    >
                      <UserMinus className="h-3.5 w-3.5" /> Dejar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Feed sidebar */}
          <aside>
            <div className="border border-slate-200 bg-white">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <h2 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider">
                  Últimos publicados
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">Lo nuevo de tus vendedores seguidos.</p>
              </div>
              {feed.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-500">
                  Cuando los vendedores que sigues publiquen, aparecerá aquí.
                </div>
              ) : (
                <ul data-testid="follows-feed" className="divide-y divide-slate-100 max-h-[600px] overflow-auto">
                  {feed.map((p) => (
                    <li key={p.product_id} className="px-4 py-3 hover:bg-slate-50">
                      <Link to={`/producto/${p.slug || p.product_id}`} className="block">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{p.brand}</div>
                        <div className="text-sm font-semibold text-slate-900 truncate hover:text-blue-700">{p.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">Ref: <span className="font-mono">{p.reference}</span></div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}
